import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useAuth0 } from "@auth0/auth0-react";
import { useParams } from "react-router-dom";
import { Message, useToaster } from "rsuite";
import CheckRoundIcon from "@rsuite/icons/CheckRound";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import {
  SectionRibbon,
  SegmentedPicker,
  GlassCard,
  Badge,
  SpacedGlassCard,
} from "../../../../ui";

export const ProcessingSettings = ({ system, onUpdate }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { id } = useParams();
  const toaster = useToaster();
  const [loading, setLoading] = useState({});

  const processingOptions = [
    {
      key: "process_edu",
      label: "ENV Processing",
      description: "Enable EDU Extraction and Processing",
      endpoint: "edu",
      enabled: system?.process_edu || false,
    },
    {
      key: "process_mag",
      label: "MAG Processing",
      description: "Enable Magnetic Resonance Data Processing",
      endpoint: "mag",
      enabled: system?.process_mag || false,
    },
    {
      key: "process_log",
      label: "LOG Processing",
      description: "Enable System Log Data Processing",
      endpoint: "log",
      enabled: system?.process_log || false,
    },
  ];

  const handleProcessingToggle = async (processingType) => {
    const processKey = `process_${processingType}`;
    setLoading((prev) => ({ ...prev, [processKey]: true }));

    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      await axiosInstance({
        method: "PUT",
        url: `/network/update/process-fields`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          sme: id,
          updateField: processingType,
        }),
      });

      toaster.push(
        <Message showIcon type="success" header="Success">
          {processingType.toUpperCase()} processing has been{" "}
          {system[processKey] ? "disabled" : "enabled"}.
        </Message>,
        "topCenter",
      );

      onUpdate && onUpdate();
    } catch (error) {
      console.error(`Failed to toggle ${processingType} processing:`, error);
      toaster.push(
        <Message showIcon type="error" header="Error">
          Failed to update {processingType.toUpperCase()} processing. Please try
          again.
        </Message>,
        "topCenter",
      );
    }

    setLoading((prev) => ({ ...prev, [processKey]: false }));
  };

  const getEnabledCount = () => {
    return processingOptions.filter((option) => option.enabled).length;
  };

  const getOverallStatus = () => {
    const enabledCount = getEnabledCount();
    if (enabledCount === 0)
      return { variant: "error", text: "All Processing Disabled" };
    if (enabledCount === processingOptions.length)
      return { variant: "success", text: "All Processing Enabled" };
    return {
      variant: "warning",
      text: `${enabledCount}/${processingOptions.length} Enabled`,
    };
  };

  const overallStatus = getOverallStatus();

  return (
    <ProcessingContainer>
      <SectionRibbon
        title="Processing Configuration"
        count={getEnabledCount()}
        countLabel={`of ${processingOptions.length} enabled`}
        variant="primary"
        rightContent={
          <Badge variant={overallStatus.variant}>{overallStatus.text}</Badge>
        }
      />

      <ProcessingGrid>
        {processingOptions.map((option) => (
          <ProcessingCard key={option.key}>
            <SpacedGlassCard>
              <CardContent>
                <CardHeader>
                  <IconSection>
                    <ProcessingIcon>{option.icon}</ProcessingIcon>
                    <StatusIndicator enabled={option.enabled}>
                      <CheckRoundIcon
                        color={option.enabled ? "#27ae60" : "#95a5a6"}
                        size="1.5em"
                      />
                    </StatusIndicator>
                  </IconSection>

                  <ProcessingTitle>{option.label}</ProcessingTitle>
                  <ProcessingDescription>
                    {option.description}
                  </ProcessingDescription>
                </CardHeader>

                <CardBody>
                  <StatusSection>
                    <StatusLabel>Current Status</StatusLabel>
                    <StatusBadge variant={option.enabled ? "success" : "error"}>
                      {option.enabled ? "Enabled" : "Disabled"}
                    </StatusBadge>
                  </StatusSection>

                  <ControlSection>
                    <ControlLabel>Processing Control</ControlLabel>
                    <ToggleControl>
                      <SegmentedPicker
                        options={[
                          { eventKey: "disabled", label: "Disabled" },
                          { eventKey: "enabled", label: "Enabled" },
                        ]}
                        activeKey={option.enabled ? "enabled" : "disabled"}
                        onSelect={(value) => {
                          if ((value === "enabled") !== option.enabled) {
                            handleProcessingToggle(option.endpoint);
                          }
                        }}
                        size="small"
                        disabled={loading[option.key]}
                      />
                    </ToggleControl>
                  </ControlSection>
                </CardBody>

                {loading[option.key] && (
                  <LoadingOverlay>
                    <LoadingSpinner>⟳</LoadingSpinner>
                    <LoadingText>Updating...</LoadingText>
                  </LoadingOverlay>
                )}
              </CardContent>
            </SpacedGlassCard>
          </ProcessingCard>
        ))}
      </ProcessingGrid>

      <SummarySection>
        <GlassCard>
          <SummaryContent>
            <SummaryHeader>
              <SummaryTitle>Processing Summary</SummaryTitle>
            </SummaryHeader>

            <SummaryGrid>
              {processingOptions.map((option) => (
                <SummaryItem key={option.key}>
                  <SummaryItemIcon>{option.icon}</SummaryItemIcon>
                  <SummaryItemLabel>{option.label}</SummaryItemLabel>
                  <SummaryItemStatus enabled={option.enabled}>
                    {option.enabled ? "Active" : "Inactive"}
                  </SummaryItemStatus>
                </SummaryItem>
              ))}
            </SummaryGrid>

            <ProcessingNote>
              <NoteText>
                Processing settings determine which data types are actively
                monitored and processed for this system. Changes take effect
                immediately.
              </NoteText>
            </ProcessingNote>
          </SummaryContent>
        </GlassCard>
      </SummarySection>
    </ProcessingContainer>
  );
};

// Styled Components
const ProcessingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const ProcessingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProcessingCard = styled.div`
  position: relative;
`;

const CardContent = styled.div`
  padding: 1.5rem;
  position: relative;
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const IconSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ProcessingIcon = styled.div`
  font-size: 2rem;
`;

const StatusIndicator = styled.div`
  opacity: ${({ enabled }) => (enabled ? 1 : 0.5)};
  transition: opacity 0.3s ease;
`;

const ProcessingTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: rgba(0, 0, 0, 0.9);
`;

const ProcessingDescription = styled.p`
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.6);
  margin: 0;
  line-height: 1.4;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const StatusSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const StatusLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatusBadge = styled(Badge)`
  align-self: flex-start;
`;

const ControlSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ControlLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ToggleControl = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: 12px;
`;

const LoadingSpinner = styled.div`
  font-size: 1.5rem;
  animation: spin 1s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.7);
  font-weight: 500;
`;

const SummarySection = styled.div`
  margin-top: 1rem;
`;

const SummaryContent = styled.div`
  padding: 1.5rem 2rem;
`;

const SummaryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SummaryIcon = styled.div`
  font-size: 1.5rem;
`;

const SummaryTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: rgba(0, 0, 0, 0.9);
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const SummaryItemIcon = styled.div`
  font-size: 1.2rem;
`;

const SummaryItemLabel = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.8);
  flex: 1;
`;

const SummaryItemStatus = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  background: ${({ enabled }) =>
    enabled ? "rgba(39, 174, 96, 0.1)" : "rgba(149, 165, 166, 0.1)"};
  color: ${({ enabled }) => (enabled ? "#27ae60" : "#95a5a6")};
`;

const ProcessingNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: rgba(52, 152, 219, 0.05);
  border-radius: 8px;
  border-left: 4px solid #3498db;
`;

const NoteIcon = styled.div`
  font-size: 1.2rem;
  margin-top: 0.1rem;
`;

const NoteText = styled.div`
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.7);
  line-height: 1.5;
`;
